/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */

import type {
  BelegungsIntervalForChart,
  ChartData,
  SweepLine,
} from '@/domain/dienstplan/features/StackedChart/utils/types';
import type {
  Arbeitsbereich,
  BelegungsIntervall,
} from '@/domain/dienstplan/shared/types/Belegung';

export function generateChartData(
  belegungen: BelegungsIntervall[],
  arbeitsbereiche: Arbeitsbereich[],
): ChartData {
  const timePoints = getAllTimePoints(belegungen);

  const sweepLine = initSweepLine(arbeitsbereiche, belegungen);
  const chartData: ChartData = initChartData(sweepLine);

  for (const currentTimePoint of timePoints) {
    const belegungenByCurrentTimePoint = getAllBelegungenByTimePoint(
      belegungen,
      currentTimePoint,
    );

    for (const currentBelegung of belegungenByCurrentTimePoint) {
      if (currentBelegung.isProcessed) {
        continue;
      }

      const arbeitsbereichId = currentBelegung.belegung.arbeitsbereich.id;

      if (currentBelegung.startX === currentTimePoint) {
        const prevY = getValueFromPrevSweepLineItem(
          sweepLine,
          currentBelegung.belegung.arbeitsbereich.id,
        );
        sweepLine[arbeitsbereichId].push({
          x: currentTimePoint,
          y: prevY,
        });
        sweepLine[arbeitsbereichId].push({
          x: currentTimePoint,
          y: currentBelegung.belegung.personenanzahlMax + prevY,
        });

        updateAboveStackItems(
          sweepLine,
          chartData,
          arbeitsbereichId,
          currentTimePoint,
        );
      }

      if (currentBelegung.endeX === currentTimePoint) {
        sweepLine[arbeitsbereichId].push({
          x: currentTimePoint,
          y: sweepLine[arbeitsbereichId][1].y,
        });
        sweepLine[arbeitsbereichId].push({
          x: currentTimePoint,
          y: sweepLine[arbeitsbereichId][0].y,
        });
        updateChartData(chartData, sweepLine, arbeitsbereichId);
        sweepLine[arbeitsbereichId] = [];

        // Prüfe, ob aktuell abgeschlossene Belegung einen Nachbar hat und bearbeite diese
        const adjacentBelegung = getAdjacentBelegungOfSameArbeitsbereich(
          currentBelegung,
          belegungenByCurrentTimePoint,
        );
        if (adjacentBelegung) {
          const arbeitsbereichId = adjacentBelegung.belegung.arbeitsbereich.id;
          const prevY = getValueFromPrevSweepLineItem(
            sweepLine,
            arbeitsbereichId,
          );
          sweepLine[arbeitsbereichId].push({
            x: adjacentBelegung.startX,
            y: prevY,
          });
          sweepLine[arbeitsbereichId].push({
            x: adjacentBelegung.startX,
            y: adjacentBelegung.belegung.personenanzahlMax + prevY,
          });

          const idOfProcessedBelegung = belegungenByCurrentTimePoint.findIndex(
            (b) => b.belegung.id === adjacentBelegung.belegung.id,
          );
          belegungenByCurrentTimePoint[idOfProcessedBelegung].isProcessed =
            true;
        }

        // Update darunter liegende Stacks
        updateAboveStackItems(
          sweepLine,
          chartData,
          arbeitsbereichId,
          currentTimePoint,
        );
      }
    }
  }

  return chartData;
}

// Sweep Line Helper Functions
function initSweepLine(
  arbeitsbereiche: Arbeitsbereich[],
  belegungen: BelegungsIntervall[],
): SweepLine {
  const sweepLine: SweepLine = {};

  arbeitsbereiche
    .map((ab) => ab.id)
    .forEach((id) => {
      const belegungByArbeitsbereichId = belegungen.find(
        (b) => b.belegung.arbeitsbereich.id === id,
      );

      if (belegungByArbeitsbereichId) {
        sweepLine[id] = [];
      }
    });

  return sweepLine;
}

function initChartData(sweepLine: SweepLine): ChartData {
  const chartData: ChartData = {};
  Object.keys(sweepLine).forEach((key) => (chartData[key] = []));
  return chartData;
}

function updateChartData(
  chartData: ChartData,
  sweepLine: SweepLine,
  arbeitsbereichId: string,
): void {
  if (!chartData[arbeitsbereichId]) {
    chartData[arbeitsbereichId] = [];
  }
  chartData[arbeitsbereichId].push(sweepLine[arbeitsbereichId]);
}

function getValueFromPrevSweepLineItem(
  sweepLine: SweepLine,
  currentArbeitsbereichId: string,
): number {
  const arbeitsbereichIdsOfSweepLine = Object.keys(sweepLine);

  if (arbeitsbereichIdsOfSweepLine.indexOf(currentArbeitsbereichId) === 0) {
    return 0;
  } else {
    // Hier anpassen, dass nach dem ersten vorherigen Element mit Wert geschaut wird
    let currentIndex =
      arbeitsbereichIdsOfSweepLine.indexOf(currentArbeitsbereichId) - 1;
    while (currentIndex >= 0) {
      const prevItem = sweepLine[arbeitsbereichIdsOfSweepLine[currentIndex]];
      if (prevItem.length > 0) {
        return Math.max(...prevItem.map((p) => p.y));
      } else {
        currentIndex = currentIndex - 1;
      }
    }
    return 0;
  }
}

function getItemsFromAboveStacks(
  sweepLine: SweepLine,
  currentArbeitsbereichId: string,
): SweepLine {
  const arbeitsbereichIdsOfSweepLine = Object.keys(sweepLine);
  if (
    arbeitsbereichIdsOfSweepLine.length === 1 ||
    arbeitsbereichIdsOfSweepLine.indexOf(currentArbeitsbereichId) ===
      arbeitsbereichIdsOfSweepLine.length - 1
  ) {
    return {};
  }

  const keysOfAboveStacks = arbeitsbereichIdsOfSweepLine.slice(
    arbeitsbereichIdsOfSweepLine.indexOf(currentArbeitsbereichId) + 1,
    arbeitsbereichIdsOfSweepLine.length,
  );
  const items: SweepLine = {};
  keysOfAboveStacks.map((key) => (items[key] = sweepLine[key]));
  return items;
}

function getAdjacentBelegungOfSameArbeitsbereich(
  currentBelegung: BelegungsIntervall,
  belegungen: BelegungsIntervall[],
): BelegungsIntervall | undefined {
  const adjacentBelegung = belegungen.filter(
    (b) =>
      b.belegung.arbeitsbereich.id ===
        currentBelegung.belegung.arbeitsbereich.id &&
      currentBelegung.endeX === b.startX,
  );
  if (adjacentBelegung.length !== 1) {
    return undefined;
  }
  return adjacentBelegung[0];
}

function updateAboveStackItems(
  sweepLine: SweepLine,
  chartData: ChartData,
  arbeitsbereichId: string,
  currentTimePoint: number,
): void {
  const prevItems = getItemsFromAboveStacks(sweepLine, arbeitsbereichId);
  Object.entries(prevItems).forEach(([arbeitsbereichId, points]) => {
    if (points.length > 0) {
      // Schließe Item
      sweepLine[arbeitsbereichId].push({
        x: currentTimePoint,
        y: points[1].y,
      });
      sweepLine[arbeitsbereichId].push({
        x: currentTimePoint,
        y: points[0].y,
      });

      // Speichere Element zu ChartData
      updateChartData(chartData, sweepLine, arbeitsbereichId);

      // Lege neues Element in SweepLine an
      const prevY = getValueFromPrevSweepLineItem(sweepLine, arbeitsbereichId);
      sweepLine[arbeitsbereichId] = [];
      sweepLine[arbeitsbereichId].push({
        x: currentTimePoint,
        y: prevY,
      });
      sweepLine[arbeitsbereichId].push({
        x: currentTimePoint,
        y: prevY + Math.abs(points[1].y - points[0].y),
      });
    }
  });
}

function getAllTimePoints(belegungen: BelegungsIntervall[]): number[] {
  return Array.from(
    new Set(belegungen.flatMap((b) => [b.startX, b.endeX])),
  ).sort((a, b) => a - b);
}

function getAllBelegungenByTimePoint(
  belegungen: BelegungsIntervall[],
  currentTimePoint: number,
): BelegungsIntervalForChart[] {
  return belegungen
    .filter((b) => b.startX <= currentTimePoint && b.endeX >= currentTimePoint)
    .sort((b1, b2) => b1.startX - b2.startX)
    .map((b) => {
      return {
        ...b,
        startX: b.startX,
        endeX: b.endeX,
        width: b.width,
        height: b.height,
        fillColor: b.fillColor,
        startY: b.startY,
        borderColor: b.borderColor,
        isProcessed: false,
      };
    });
}
